import { ReactComponent as StarIcon } from '../assets/star.svg';
import { ReactComponent as GitHubIcon } from '../assets/github.svg';

interface Project {
  name: string;
  description: string;
  details: string;
  githubUrl: string;
  stars?: number;
}

function OpenSourceContributions() {
  const projects: Project[] = [
    {
      name: "Fuel2 - DDD Product Development AI Framework",
      description: "AI-assisted Domain-Driven Design framework",
      details: "End-to-end AI framework automating product development from domain exploration to implementation, including event storming, bounded context modeling, and CI/CD scaffolding.",
      githubUrl: "https://github.com/fuel2/fuel2"
    },
    {
      name: "Seiretsu",
      description: "Subtitle precision and speech alignment tool",
      details: "Improved subtitle precision and speech alignment accuracy using a lightweight OpenAI Whisper model and ported enhancements to TypeScript.",
      githubUrl: "https://github.com/qtvhao/seiretsu"
    },
    {
      name: "Online Video Platform",
      description: "Adaptive bitrate proxy and streaming components",
      details: "Developed an adaptive bitrate proxy and related components (transcoding, caching, load balancing) to optimize video streaming.",
      githubUrl: "https://github.com/online-video-platform/Adaptive-Bitrate-Proxy"
    },
    {
      name: "VideoAutoComposition",
      description: "Automated media composition with distributed processing",
      details: "Automated media composition with Shell scripts and distributed processing for seamless video creation.",
      githubUrl: "https://github.com/qtvhao/VideoAutoComposition"
    },
    {
      name: "PiCloud",
      description: "Kubernetes-based home-server management solution",
      details: "Created a Kubernetes-based home-server management solution integrating Terraform, CI/CD, and secure container management.",
      githubUrl: "https://github.com/qtvhao/picloud",
      stars: 13
    }
  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-8">Open Source Contributions</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {projects.map((project: Project, index: number) => (
          <div key={index} className="rounded-xl transition-all duration-300 p-6 border border-gray-100 group">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <h3 className="text-xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors">{project.name}</h3>
                {project.stars && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    <StarIcon className="w-3 h-3" />
                    <span>{project.stars}</span>
                  </div>
                )}
              </div>
              <a 
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-gray-100 hover:bg-blue-100 rounded-lg transition-all duration-200 hover:scale-110"
                title="View on GitHub"
              >
                <GitHubIcon className="w-5 h-5 text-gray-600 hover:text-blue-600 transition-colors" />
              </a>
            </div>
            
            <div className="mb-4">
              <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-3">
                {project.description}
              </div>
              <p className="text-gray-700 leading-relaxed">{project.details}</p>
            </div>
            
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center text-sm text-gray-500">
                Open Source
              </div>
              <a 
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium shadow-md hover:shadow-lg"
              >
                View Project
              </a>
            </div>
          </div>
        ))}
      </div>
      
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center justify-center">
            <span className="mr-2">📚</span>
            Explore More Projects
          </h3>
          <p className="text-gray-600 mb-4">Discover additional open source contributions and detailed project documentation</p>
          <a 
            href="https://docs.google.com/document/d/1WLOCrJ2nt0Mpb-dDuQa9hjJ9s1dok_kgTxC1_OHdfeo/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            View Full Portfolio
          </a>
        </div>
      </div>
    </div>
  );
}

export default OpenSourceContributions;